import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Upload, UtensilsCrossed, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COURSES = ["Starters", "Mains", "Desserts", "Sides", "Snacks"];

const emptyForm = { name: "", category: "", description: "", allergens: "", key_ingredients: "", guest_description: "", suggested_pairings: "" };

export default function FoodPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCourse, setFilterCourse] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/food`);
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(f => {
    const q = search.toLowerCase();
    const matchSearch = !q || f.name.toLowerCase().includes(q) || (f.description || "").toLowerCase().includes(q);
    const matchCourse = filterCourse === "all" || f.category === filterCourse;
    return matchSearch && matchCourse;
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ name: f.name, category: f.category || "", description: f.description || "", allergens: f.allergens || "", key_ingredients: f.key_ingredients || "", guest_description: f.guest_description || "", suggested_pairings: f.suggested_pairings || "" }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/food/${editing.id}`, form);
        toast.success("Food item updated");
      } else {
        await axios.post(`${API}/food`, form);
        toast.success("Food item added");
      }
      setDialogOpen(false);
      load();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this food item?")) return;
    await axios.delete(`${API}/food/${id}`);
    toast.success("Food item deleted");
    load();
  };

  const uploadImage = async (foodId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await axios.post(`${API}/food/${foodId}/upload-image`, fd);
    toast.success("Image uploaded");
    load();
  };

  const uploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/food/upload-csv`, fd);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "CSV upload failed"); }
    e.target.value = "";
  };

  const F = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Food Library</h1>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={uploadCSV} className="hidden" data-testid="food-csv-upload" />
            <Button variant="outline" size="sm" asChild><span><Upload size={14} className="mr-1" /> CSV</span></Button>
          </label>
          <Button size="sm" onClick={openCreate} data-testid="add-food-btn"><Plus size={14} className="mr-1" /> Add Food</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search food..." className="pl-9 bg-[hsl(var(--secondary))]" data-testid="food-search" />
        </div>
        <Select value={filterCourse} onValueChange={setFilterCourse}>
          <SelectTrigger className="w-[140px] bg-[hsl(var(--secondary))]" data-testid="food-course-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Courses</SelectItem>
            {COURSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Food list */}
      <div className="grid gap-3">
        {filtered.length === 0 && <p className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">{items.length === 0 ? "No food items yet. Add one or upload a CSV." : "No items match your search."}</p>}
        {filtered.map(f => (
          <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]" data-testid={`food-item-${f.id}`}>
            <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-[hsl(var(--background))] flex items-center justify-center border border-[hsl(var(--border))]">
              {f.image_url ? <img src={`${BACKEND}${f.image_url}`} alt={f.name} className="w-full h-full object-cover" /> : <UtensilsCrossed size={20} className="text-[hsl(var(--muted-foreground))]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[hsl(var(--foreground))] truncate">{f.name}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{[f.category, f.allergens ? `Allergens: ${f.allergens}` : null].filter(Boolean).join(" · ")}</p>
            </div>
            <label className="cursor-pointer shrink-0">
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(f.id, e.target.files[0])} className="hidden" />
              <Button variant="ghost" size="icon" asChild><span><ImageIcon size={14} /></span></Button>
            </label>
            <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-food-${f.id}`}><Pencil size={14} /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(f.id)} data-testid={`delete-food-${f.id}`}><Trash2 size={14} className="text-red-400" /></Button>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <DialogHeader><DialogTitle>{editing ? "Edit Food Item" : "Add Food Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => F("name", e.target.value)} className="bg-[hsl(var(--secondary))]" data-testid="food-name-input" /></div>
            <div>
              <Label>Course</Label>
              <Select value={form.category} onValueChange={v => F("category", v)}>
                <SelectTrigger className="bg-[hsl(var(--secondary))]"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{COURSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => F("description", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Key Ingredients</Label><Input value={form.key_ingredients} onChange={e => F("key_ingredients", e.target.value)} placeholder="e.g. Wagyu beef, truffle, parmesan" className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Allergens</Label><Input value={form.allergens} onChange={e => F("allergens", e.target.value)} placeholder="e.g. Contains dairy, gluten" className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Guest Description</Label><Textarea value={form.guest_description} onChange={e => F("guest_description", e.target.value)} placeholder="How staff should describe this dish..." className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Suggested Pairings</Label><Textarea value={form.suggested_pairings} onChange={e => F("suggested_pairings", e.target.value)} placeholder="Wine or cocktail pairings..." className="bg-[hsl(var(--secondary))]" /></div>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="food-save-btn">{saving ? "Saving..." : editing ? "Update Item" : "Add Item"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
